import { Rocket, Check, ArrowRight, CalendarRange } from "lucide-react";
import { FadeUp, Eyebrow } from "./Reveal";
import WaveField from "./Waves";
import { useLang } from "@/i18n";

const PLANS = [
  { n: "p1_n", b: "p1_b", feats: ["p1_1", "p1_2", "p1_3"], badge: null },
  { n: "p2_n", b: "p2_b", feats: ["p2_1", "p2_2", "p2_3"], badge: "p2_badge" },
  { n: "p3_n", b: "p3_b", feats: ["p3_1", "p3_2", "p3_3"], badge: null },
  { n: "p4_n", b: "p4_b", feats: ["p4_1", "p4_2", "p4_3"], badge: null },
];

const KICK_FEATS = ["kick_1", "kick_2", "kick_3", "kick_4"];
const TERMS = ["terms_1", "terms_2", "terms_3", "terms_4"];

export default function Plans() {
  const { t } = useLang();
  const goContact = () => window.__lenis?.scrollTo("#contact", { offset: -96 });

  return (
    <section id="plans" data-testid="plans-section" className="relative py-24 sm:py-36 bg-[#FFFFFF]/70 border-y border-[#2B0A4E]/8">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <FadeUp className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <Eyebrow>{t("plans_eyebrow")}</Eyebrow>
            <h2 className="mt-4 font-serif-display font-bold text-3xl sm:text-4xl lg:text-5xl text-[#2B0A4E] max-w-3xl leading-tight" data-testid="plans-heading">
              {t("plans_h1")} <span className="italic gold-highlight">{t("plans_h2")}</span>
            </h2>
          </div>
          <p className="max-w-xs text-sm text-[#5E4783] leading-relaxed">{t("plans_sub")}</p>
        </FadeUp>

        <FadeUp delay={0.1}>
          <div data-testid="kickstart-card" className="relative mt-14 sketch-border bg-[#2B0A4E] text-[#FDFBF5] p-8 sm:p-12 overflow-hidden grid lg:grid-cols-2 gap-10 items-center">
            <WaveField variant="dark" className="absolute -bottom-20 -right-10 w-[420px] opacity-20 pointer-events-none" />
            <div className="relative">
              <span className="inline-flex items-center gap-2 font-mono-label text-[10px] tracking-[0.2em] uppercase bg-[#F2C230] text-[#2B0A4E] px-3 py-1.5 rounded-full">
                <Rocket size={13} /> {t("kick_tag")}
              </span>
              <h3 className="mt-4 font-serif-display font-bold text-3xl sm:text-4xl" data-testid="kickstart-title">
                {t("kick_h")}
              </h3>
              <p className="mt-3 text-sm sm:text-base text-[#FDFBF5]/75 leading-relaxed max-w-md">
                {t("kick_d")}
              </p>
              <p className="mt-4 text-sm text-[#F2C230] leading-relaxed max-w-md">{t("kick_note")}</p>
              <button
                data-testid="kickstart-cta"
                onClick={goContact}
                className="mt-6 inline-flex items-center gap-2 sketch-border bg-[#F2C230] text-[#2B0A4E] px-7 py-3.5 font-mono-label text-xs tracking-[0.2em] uppercase hover:bg-[#FDFBF5] hover:-translate-y-0.5 transition-[transform,background-color] duration-300"
              >
                {t("kick_cta")} <ArrowRight size={15} />
              </button>
            </div>
            <ul className="relative grid gap-3">
              {KICK_FEATS.map((k, i) => (
                <li key={k} data-testid={`kickstart-feat-${i + 1}`} className="flex items-start gap-3 bg-[#FDFBF5]/[0.06] rounded-xl px-4 py-3">
                  <Check size={17} className="text-[#F2C230] shrink-0 mt-0.5" />
                  <span className="text-sm sm:text-base text-[#FDFBF5]/90">{t(k)}</span>
                </li>
              ))}
            </ul>
          </div>
        </FadeUp>

        <FadeUp className="mt-16">
          <p className="font-mono-label text-xs tracking-[0.25em] uppercase text-[#2B0A4E]/60">{t("plans_step2")}</p>
          <p className="mt-2 text-base sm:text-lg text-[#5E4783]">{t("plans_pick")}</p>
        </FadeUp>

        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {PLANS.map((p, i) => (
            <FadeUp key={p.n} delay={0.07 * i}>
              <div
                data-testid={`plan-card-${t(p.n).toLowerCase()}`}
                className={`relative sketch-border bg-[#FFFFFF] p-6 sm:p-7 h-full flex flex-col hover:-translate-y-1.5 transition-transform duration-300 ${
                  p.badge ? "shadow-[6px_6px_0_#F2C230]" : "hover:shadow-[6px_6px_0_#8B5CF6]"
                }`}
              >
                {p.badge && (
                  <span data-testid="plan-badge-recommended" className="absolute -top-3 right-4 font-mono-label text-[9px] tracking-[0.2em] uppercase bg-[#F2C230] text-[#2B0A4E] px-3 py-1 rounded-full border border-[#2B0A4E]/20">
                    {t(p.badge)}
                  </span>
                )}
                <h4 className="font-serif-display font-bold text-2xl text-[#2B0A4E]">{t(p.n)}</h4>
                <p className="mt-1 font-mono-label text-[9px] tracking-[0.2em] uppercase text-[#8B5CF6]">{t("best_for")}</p>
                <p className="mt-1 text-sm text-[#5E4783] leading-snug">{t(p.b)}</p>
                <ul className="mt-4 space-y-2.5 flex-1">
                  {p.feats.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-[#2B0A4E]/85">
                      <Check size={15} className="text-[#8B5CF6] shrink-0 mt-0.5" />
                      {t(f)}
                    </li>
                  ))}
                </ul>
                <button
                  data-testid={`plan-quote-${t(p.n).toLowerCase()}`}
                  onClick={goContact}
                  className="mt-5 inline-flex items-center gap-1.5 font-mono-label text-[10px] tracking-[0.15em] uppercase text-[#2B0A4E] border-b-2 border-[#F2C230] pb-0.5 self-start hover:text-[#8B5CF6] hover:border-[#8B5CF6] transition-colors duration-300"
                >
                  {t("plans_cta")} <ArrowRight size={12} />
                </button>
              </div>
            </FadeUp>
          ))}
        </div>

        <div className="mt-10 grid md:grid-cols-2 gap-6">
          <FadeUp delay={0.1}>
            <div data-testid="individual-services-card" className="sketch-border bg-[#F0E9FF] p-7 sm:p-8 h-full">
              <h4 className="font-serif-display font-semibold text-xl sm:text-2xl text-[#2B0A4E]">{t("ind_h")}</h4>
              <p className="mt-3 font-mono-label text-[10px] sm:text-[11px] tracking-[0.15em] uppercase text-[#2B0A4E]/70 leading-relaxed">
                {t("ind_items")}
              </p>
              <button
                data-testid="individual-quote-btn"
                onClick={goContact}
                className="mt-5 inline-flex items-center gap-1.5 font-mono-label text-[10px] tracking-[0.15em] uppercase text-[#2B0A4E] border-b-2 border-[#F2C230] pb-0.5 hover:text-[#8B5CF6] hover:border-[#8B5CF6] transition-colors duration-300"
              >
                {t("plans_cta")} <ArrowRight size={12} />
              </button>
            </div>
          </FadeUp>
          <FadeUp delay={0.2}>
            <div data-testid="event-card" className="sketch-border bg-[#FFFFFF] p-7 sm:p-8 h-full">
              <span className="inline-flex items-center gap-2 font-mono-label text-[9px] tracking-[0.2em] uppercase text-[#8B5CF6]">
                <CalendarRange size={13} /> One-time project
              </span>
              <h4 className="mt-2 font-serif-display font-semibold text-xl sm:text-2xl text-[#2B0A4E]">{t("event_h")}</h4>
              <p className="mt-2 text-sm sm:text-base text-[#5E4783] leading-relaxed">{t("event_d")}</p>
              <button
                data-testid="event-quote-btn"
                onClick={goContact}
                className="mt-5 inline-flex items-center gap-1.5 font-mono-label text-[10px] tracking-[0.15em] uppercase text-[#2B0A4E] border-b-2 border-[#F2C230] pb-0.5 hover:text-[#8B5CF6] hover:border-[#8B5CF6] transition-colors duration-300"
              >
                {t("plans_cta")} <ArrowRight size={12} />
              </button>
            </div>
          </FadeUp>
        </div>

        <FadeUp delay={0.15} className="mt-10">
          <div className="flex flex-wrap gap-3" data-testid="plans-terms">
            {TERMS.map((k) => (
              <span key={k} className="inline-flex items-center gap-2 font-mono-label text-[9px] sm:text-[10px] tracking-[0.12em] uppercase bg-[#FFFFFF] sketch-border px-4 py-2.5 text-[#2B0A4E]/80">
                <Check size={13} className="text-[#8B5CF6]" /> {t(k)}
              </span>
            ))}
          </div>
        </FadeUp>
      </div>
    </section>
  );
}
